// Login.js - Updated with shadcn/ui and navy blue color palette
import React, { useState, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { Card } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import './Login.css';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState('');

  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validation
    if (!email || !password) {
      setError('Please fill in all fields');
      setLoading(false);
      return;
    }

    try {
      await login(email, password);
      navigate('/events'); // Redirect to events page after successful login
    } catch (err) {
      // Handle specific Firebase errors
      switch (err.code) {
        case 'auth/invalid-credential':
          setError('The email or password you entered is incorrect. Please try again.');
          break;
        case 'auth/user-not-found':
          setError('No account found with this email address. Please check your email or register for a new account.');
          break;
        case 'auth/wrong-password':
          setError('The password you entered is incorrect. Please try again or reset your password.');
          break;
        case 'auth/invalid-email':
          setError('Please enter a valid email address.');
          break;
        case 'auth/user-disabled':
          setError('Your account has been disabled. Please contact support for assistance.');
          break;
        case 'auth/too-many-requests':
          setError('Too many failed login attempts. Please try again in a few minutes or reset your password.');
          break;
        case 'auth/network-request-failed':
          setError('Network error. Please check your internet connection and try again.');
          break;
        default:
          setError('Unable to login. Please check your credentials and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setResetMessage('');

    if (!resetEmail) {
      setError('Please enter your email address');
      return;
    }

    try {
      const { resetPassword } = await import('../api/auth');
      await resetPassword(resetEmail);
      setResetMessage('Password reset email sent! Check your inbox.');
      setResetEmail('');
      setTimeout(() => {
        setShowForgotPassword(false);
        setResetMessage('');
      }, 3000);
    } catch (err) {
      switch (err.code) {
        case 'auth/user-not-found':
          setError('No account found with this email address. Please check your email or contact support.');
          break;
        case 'auth/invalid-email':
          setError('Please enter a valid email address.');
          break;
        case 'auth/network-request-failed':
          setError('Network error. Please check your internet connection and try again.');
          break;
        default:
          setError('Unable to send password reset email. Please try again or contact support.');
      }
    }
  };

  if (showForgotPassword) {
    return (
      <div className="login-page">
        <div className="login-container">
          <Card className="login-card">
            <div className="login-header">
              <div className="login-logo">
                <img src="/logo.svg" alt="KOS Logo" className="logo-image" />
              </div>
              <h1 className="login-title">Reset Password</h1>
              <p className="login-subtitle">
                Enter your email address and we'll send you a link to reset your password.
              </p>
            </div>

            {error && <div className="alert alert-error">{error}</div>}
            {resetMessage && <div className="alert alert-success">{resetMessage}</div>}

            <form onSubmit={handleForgotPassword} className="auth-form">
              <div className="form-group">
                <label htmlFor="resetEmail" className="form-label">
                  Email Address
                </label>
                <Input
                  id="resetEmail"
                  type="email"
                  placeholder="Enter your email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  required
                />
              </div>

              <Button type="submit" className="w-full">
                Send Reset Link
              </Button>

              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => {
                  setShowForgotPassword(false);
                  setError('');
                  setResetMessage('');
                }}
              >
                Back to Login
              </Button>
            </form>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-container">
        <Card className="login-card">
          <div className="login-header">
            <div className="login-logo">
              <img src="/logo.svg" alt="KOS Logo" className="logo-image" />
            </div>
            <h1 className="login-title">Sign in to your KOS account</h1>
            <p className="login-subtitle">Enter your credentials to continue</p>
          </div>

          {error && <div className="alert alert-error">{error}</div>}

          <form onSubmit={handleSubmit} className="auth-form">
            <div className="form-group">
              <label htmlFor="email" className="form-label">
                Email Address
              </label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </div>

            <div className="form-group">
              <label htmlFor="password" className="form-label">
                Password
              </label>
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
            </div>

            <div className="form-footer">
              <Button
                type="button"
                variant="link"
                onClick={() => setShowForgotPassword(true)}
              >
                Forgot Password?
              </Button>
            </div>

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <div className="login-footer">
            <p className="text-center">
              Don't have an account?{' '}
              <Link to="/register" className="link-primary">
                Register here
              </Link>
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Login;
